// ==============================================
// API Service Layer
// ==============================================

import { 
  ApiResponse, 
  User, 
  LoginFormData, 
  RegisterFormData,
  Club,
  City,
  Tournament,
  Booking,
  BookingFormData,
  TrainerProfile,
  TrainingBooking,
  Match,
  MatchFormData,
  MatchSet,
  RankingEntry,
  Notification,
  PlayerLevel,
  TournamentType,
  MatchType,
  OpenMatchRequest,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const AUTH_TOKEN_KEY = 'tt_auth_token';

function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(AUTH_TOKEN_KEY);
}

// ==============================================
// Helper Functions
// ==============================================

async function fetchAPI<T>(
  endpoint: string, 
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = getAuthToken();
  const headers = new Headers(options.headers || undefined);

  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const defaultOptions: RequestInit = {
    credentials: 'include', // Include cookies for session
    ...options,
    headers,
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, defaultOptions);
    const data = await response.json();
    
    if (!response.ok) {
      if (response.status === 401 && typeof window !== 'undefined') {
        sessionStorage.removeItem(AUTH_TOKEN_KEY);
      }
      throw new Error(data.message || 'API request failed');
    }
    
    return data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  } 
}

// ==============================================
// Authentication API
// ==============================================

export const authAPI = {
  register: async (formData: RegisterFormData): Promise<ApiResponse<{ user: User; token: string }>> => {
    return fetchAPI('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(formData),
    });
  },

  login: async (credentials: LoginFormData): Promise<ApiResponse<{ user: User; token: string }>> => {
    return fetchAPI('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  },

  logout: async (): Promise<ApiResponse> => {
    return fetchAPI('/api/auth/logout', {
      method: 'POST',
    });
  },

  getCurrentUser: async (): Promise<ApiResponse<User>> => {
    return fetchAPI('/api/auth/me');
  },
};

// ==============================================
// Users API
// ==============================================

export const usersAPI = {
  updateProfile: async (data: {
    first_name?: string;
    last_name?: string;
    phone?: string;
    external_profile_url?: string;
  }): Promise<ApiResponse<any>> => {
    return fetchAPI('/api/users', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<ApiResponse<any>> => {
    return fetchAPI('/api/users', {
      method: 'PUT',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  },

  uploadAvatar: async (file: File): Promise<ApiResponse<{ avatar_url: string }>> => {
    const formData = new FormData();
    formData.append('avatar', file);
    const token = getAuthToken();
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await fetch(`${API_BASE_URL}/api/users/avatar`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Upload failed');
    return data;
  },
};

// ==============================================
// Metadata API
// ==============================================

export const metadataAPI = {
  getAll: async (): Promise<ApiResponse<{
    player_levels: PlayerLevel[];
    tournament_types: TournamentType[];
    match_types: MatchType[];
  }>> => {
    return fetchAPI('/api/metadata');
  },
};

// ==============================================
// Cities API
// ==============================================

export const citiesAPI = {
  getAll: async (): Promise<ApiResponse<{ cities: City[] }>> => {
    return fetchAPI('/api/cities');
  },

  getById: async (id: number): Promise<ApiResponse<City>> => {
    return fetchAPI(`/api/cities/${id}`);
  },
};

// ==============================================
// Clubs API
// ==============================================

export const clubsAPI = {
  getAll: async (params?: {
    city_id?: number;
    lat?: number;
    lng?: number;
    radius?: number;
  }): Promise<ApiResponse<{ clubs: Club[] }>> => {
    const queryParams = new URLSearchParams();
    if (params?.city_id) queryParams.append('city_id', params.city_id.toString());
    if (params?.lat) queryParams.append('lat', params.lat.toString());
    if (params?.lng) queryParams.append('lng', params.lng.toString());
    if (params?.radius) queryParams.append('radius', params.radius.toString());
    
    const query = queryParams.toString();
    return fetchAPI(`/api/clubs${query ? `?${query}` : ''}`);
  },

  getById: async (id: number): Promise<ApiResponse<Club>> => {
    return fetchAPI(`/api/clubs/${id}`);
  },

  getTables: async (clubId: number, status?: string): Promise<ApiResponse<{ tables: any[] }>> => {
    const query = status ? `?status=${status}` : '';
    return fetchAPI(`/api/clubs/${clubId}/tables${query}`);
  },

  create: async (clubData: Partial<Club>): Promise<ApiResponse<Club>> => {
    return fetchAPI('/api/clubs', {
      method: 'POST',
      body: JSON.stringify(clubData),
    });
  },
};

// ==============================================
// Bookings API
// ==============================================

export const bookingsAPI = {
  getAll: async (params?: {
    status?: string;
    date_from?: string;
    date_to?: string;
    my_bookings?: boolean;
  }): Promise<ApiResponse<{ bookings: Booking[] }>> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.my_bookings) queryParams.append('my_bookings', '1');

    const query = queryParams.toString();
    return fetchAPI(`/api/bookings${query ? `?${query}` : ''}`);
  },

  getById: async (id: number): Promise<ApiResponse<Booking>> => {
    return fetchAPI(`/api/bookings/${id}`);
  },

  create: async (bookingData: BookingFormData): Promise<ApiResponse<Booking>> => {
    return fetchAPI('/api/bookings', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  },

  update: async (id: number, data: Partial<Booking>): Promise<ApiResponse<Booking>> => {
    return fetchAPI(`/api/bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: number): Promise<ApiResponse> => {
    return fetchAPI(`/api/bookings/${id}`, {
      method: 'DELETE',
    });
  },

  getClubSlots: async (clubId: number, date: string): Promise<ApiResponse<{ slots: Array<{ table_id: number; start_time: string; end_time: string }> }>> => {
    return fetchAPI(`/api/bookings?slots=1&club_id=${clubId}&date=${date}`);
  },

  updateParticipants: async (id: number, participantIds: number[]): Promise<ApiResponse<any>> => {
    return fetchAPI(`/api/bookings?id=${id}`, {
      method: 'PUT',
      body: JSON.stringify({ participant_ids: participantIds }),
    });
  },
};

// ==============================================
// Tournaments API
// ==============================================

export const tournamentsAPI = {
  getAll: async (params?: {
    status?: string;
    category?: string;
    club_id?: number;
  }): Promise<ApiResponse<{ tournaments: Tournament[] }>> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.club_id) queryParams.append('club_id', params.club_id.toString());
    
    const query = queryParams.toString();
    return fetchAPI(`/api/tournaments${query ? `?${query}` : ''}`);
  },

  getById: async (id: number): Promise<ApiResponse<Tournament>> => {
    return fetchAPI(`/api/tournaments/${id}`);
  },

  create: async (tournamentData: Partial<Tournament>): Promise<ApiResponse<Tournament>> => {
    return fetchAPI('/api/tournaments', {
      method: 'POST',
      body: JSON.stringify(tournamentData),
    });
  },

  join: async (tournamentId: number): Promise<ApiResponse> => {
    return fetchAPI(`/api/tournaments/${tournamentId}/join`, {
      method: 'POST',
    });
  },

  leave: async (tournamentId: number): Promise<ApiResponse> => {
    return fetchAPI(`/api/tournaments/${tournamentId}/join`, {
      method: 'DELETE',
    });
  },
};

// ==============================================
// Trainers API
// ==============================================

export const trainersAPI = {
  getAll: async (params?: {
    city_id?: number;
    status?: string;
  }): Promise<ApiResponse<{ trainers: TrainerProfile[] }>> => {
    const queryParams = new URLSearchParams();
    if (params?.city_id) queryParams.append('city_id', params.city_id.toString());
    if (params?.status) queryParams.append('status', params.status);
    
    const query = queryParams.toString();
    return fetchAPI(`/api/trainers${query ? `?${query}` : ''}`);
  },

  getBookings: async (trainerId?: number, status?: string): Promise<ApiResponse<TrainingBooking[]>> => {
    let url = '/api/trainers/bookings';
    if (trainerId) url += `/${trainerId}`;
    if (status) url += `?status=${status}`;
    return fetchAPI(url);
  },

  createBooking: async (bookingData: Partial<TrainingBooking>): Promise<ApiResponse<TrainingBooking>> => {
    return fetchAPI('/api/trainers/bookings', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  },

  updateBooking: async (id: number, data: Partial<TrainingBooking>): Promise<ApiResponse<TrainingBooking>> => {
    return fetchAPI(`/api/trainers/bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

// ==============================================
// Matches API
// ==============================================

export const matchesAPI = {
  getAll: async (params?: {
    user_id?: number;
    status?: string;
    tournament_id?: number;
    match_type_id?: number;
    match_type_slug?: string;
    mine?: boolean;
  }): Promise<ApiResponse<{ matches: Match[] }>> => {
    const queryParams = new URLSearchParams();
    if (params?.user_id) queryParams.append('user_id', params.user_id.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.tournament_id) queryParams.append('tournament_id', params.tournament_id.toString());
    if (params?.match_type_id) queryParams.append('match_type_id', params.match_type_id.toString());
    if (params?.match_type_slug) queryParams.append('match_type_slug', params.match_type_slug);
    if (params?.mine) queryParams.append('mine', '1');
    
    const query = queryParams.toString();
    return fetchAPI(`/api/matches${query ? `?${query}` : ''}`);
  },

  getById: async (id: number): Promise<ApiResponse<Match>> => {
    return fetchAPI(`/api/matches/${id}`);
  },

  create: async (matchData: MatchFormData): Promise<ApiResponse<{ match_id: number; status: Match['status']; confirmation_status: Match['confirmation_status'] }>> => {
    return fetchAPI('/api/matches', {
      method: 'POST',
      body: JSON.stringify(matchData),
    });
  },

  update: async (id: number, matchData: MatchFormData): Promise<ApiResponse<{ status: Match['status']; confirmation_status: Match['confirmation_status'] }>> => {
    return fetchAPI(`/api/matches/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ action: 'update_match', ...matchData }),
    });
  },

  updateResult: async (id: number, data: { sets: MatchSet[]; notes?: string | null }): Promise<ApiResponse> => {
    return fetchAPI(`/api/matches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  confirmResult: async (id: number): Promise<ApiResponse> => {
    return fetchAPI(`/api/matches/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ action: 'confirm' }),
    });
  },

  disputeResult: async (id: number): Promise<ApiResponse> => {
    return fetchAPI(`/api/matches/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ action: 'dispute' }),
    });
  },

  delete: async (id: number): Promise<ApiResponse> => {
    return fetchAPI(`/api/matches/${id}`, {
      method: 'DELETE',
    });
  },

  getOpenMatches: async (params?: { mine?: boolean; status?: string }): Promise<ApiResponse<{ open_matches: OpenMatchRequest[] }>> => {
    const queryParams = new URLSearchParams();
    if (params?.mine) queryParams.append('mine', '1');
    if (params?.status) queryParams.append('status', params.status);
    const query = queryParams.toString();
    return fetchAPI(`/api/matches/open${query ? `?${query}` : ''}`);
  },

  createOpenMatch: async (payload: {
    requested_match_type_slug: 'friendly' | 'competitive';
    allowed_level_ids: number[];
    is_flexible_time: boolean;
    requested_start_datetime?: string | null;
    requested_end_datetime?: string | null;
    notes?: string | null;
  }): Promise<ApiResponse<{ open_match_id: number }>> => {
    return fetchAPI('/api/matches/open', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateOpenMatch: async (id: number, action: 'accept' | 'close' | 'cancel'): Promise<ApiResponse<{ match_id?: number }>> => {
    return fetchAPI(`/api/matches/open?id=${id}`, {
      method: 'PUT',
      body: JSON.stringify({ action }),
    });
  },
};

// ==============================================
// Rankings API
// ==============================================

export const rankingsAPI = {
  getAll: async (params?: {
    level?: string;
    limit?: number;
  }): Promise<ApiResponse<{ rankings: RankingEntry[] }>> => {
    const queryParams = new URLSearchParams();
    if (params?.level) queryParams.append('level', params.level);
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const query = queryParams.toString();
    return fetchAPI(`/api/rankings${query ? `?${query}` : ''}`);
  },
};

// ==============================================
// Players API
// ==============================================

export const playersAPI = {
  search: async (params?: {
    search?: string;
    level?: string;
  }): Promise<ApiResponse<{ players: User[] }>> => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.level) queryParams.append('level', params.level);

    const query = queryParams.toString();
    return fetchAPI(`/api/players${query ? `?${query}` : ''}`);
  },

  getProfile: async (playerId: number): Promise<ApiResponse<any>> => {
    return fetchAPI(`/api/players/${playerId}/profile`);
  },

  updateLevel: async (levelId: number): Promise<ApiResponse<any>> => {
    return fetchAPI('/api/players', {
      method: 'PUT',
      body: JSON.stringify({ level_id: levelId }),
    });
  },
};

// ==============================================
// Notifications API
// ==============================================

export const notificationsAPI = {
  getAll: async (params?: { unreadOnly?: boolean; limit?: number }): Promise<ApiResponse<{ notifications: Notification[]; unread_count: number }>> => {
    const queryParams = new URLSearchParams();
    if (params?.unreadOnly) queryParams.append('unread_only', '1');
    if (params?.limit) queryParams.append('limit', String(params.limit));
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return fetchAPI(`/api/notifications${query}`);
  },

  markAsRead: async (id: number): Promise<ApiResponse> => {
    return fetchAPI(`/api/notifications/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ is_read: 1 }),
    });
  },

  markAllAsRead: async (): Promise<ApiResponse> => {
    return fetchAPI('/api/notifications', {
      method: 'PUT',
    });
  },
};
